import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { connect } from 'react-redux';

import { Section } from 'jbc-front/components/Form';
import ActionButton from 'jbc-front/components/ActionButton';
import Loading from 'src/components/Loading';
import { fetchMonthlyExpenses, getChartData, setDashboardData } from 'src/reducers/dashboard';
import { calcSumExpensesData, filterExpensesData, PARSE_DATE_FORMAT } from 'src/utils/Dashboard';

import { ChartTable } from './ChartTable';
import styles from './ExpensesMonthly.scss';
import ExpensesMonthlyChart from './ExpensesMonthlyChart';
import MonthlyExpenseMonthSelector from './MonthlyExpenseMonthSelector';
import MonthlyExpenseStatus from './MonthlyExpenseStatus';

const ExpensesMonthly = ({ monthlyExpenseSummary, chartData, setChart, fetchExpensesMonthly }) => {
  const { t } = useTranslation();
  const currentMonth = moment(monthlyExpenseSummary.date, PARSE_DATE_FORMAT);

  useEffect(() => {
    setChart('expensesMonthly', filterExpensesData(monthlyExpenseSummary));
  }, []);

  // Disabled item props from chartTable
  const [disabledItems, setDisabledItems] = useState([]);
  const [targetMonth, setTargetMonth] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const handleChangeMonth = async (targetType = 'current') => {
    setIsLoading(true);

    const adjustmentMap = { prev: -1, next: 1, current: 0 };
    const adjustment = adjustmentMap[targetType];
    const target = adjustment === 0 ? 0 : targetMonth + adjustment;

    await fetchExpensesMonthly(target);
    setTargetMonth(target);
    setIsLoading(false);
  };

  const hasNoExpensesData = () => chartData?.data?.length === 0;

  return (
    <div className="u-mt30">
      <Section title={t('features.Dashbord.components.ExpensesMonthly.monthly_expenses')} className={styles.section}>
        <div className={styles.toCurrentMonthButton}>
          <ActionButton
            as="button"
            aria-label="current"
            id="to-current-month"
            style={{ padding: '0px 5px', fontSize: '12px' }}
            onClick={() => handleChangeMonth('current')}
          >
            {t('features.Dashbord.components.ExpensesMonthly.this_month')}
          </ActionButton>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <div className={styles.container}>
              <div className={styles.width50}>
                <MonthlyExpenseMonthSelector
                  isConfirmed={chartData?.isConfirmed}
                  handleChangeMonth={handleChangeMonth}
                  currentMonth={currentMonth}
                  selectedDate={chartData}
                />
                <div className="u-mt20">
                  {t('features.Dashbord.components.ExpensesMonthly.total_expenses')}
                  {disabledItems.length > 0 &&
                    ` (${chartData?.data.length - disabledItems.length} ${t('features.Dashbord.components.ExpensesMonthly.of')} ${chartData?.data.length})`}
                </div>
                <div id="total-expenses" className={styles.totalExpenses}>
                  {`${calcSumExpensesData(chartData?.data, disabledItems)}
            ${t('features.Dashbord.components.ExpensesMonthly.vnd')}${!chartData?.isConfirmed && !hasNoExpensesData() ? '*' : ''}`}
                </div>
                <MonthlyExpenseStatus isConfirmed={chartData?.isConfirmed} hasNoExpensesData={hasNoExpensesData} unConfirmedList={chartData?.unConfirmedList} />
              </div>

              {!hasNoExpensesData() ? (
                <ExpensesMonthlyChart selectedExpensesData={chartData?.data} disabledItems={disabledItems} />
              ) : (
                <div className={styles.width50} style={{ alignSelf: 'center' }}>
                  <div className={styles.noData}>{t('features.Dashbord.components.ExpensesMonthly.no_data')}</div>
                </div>
              )}
            </div>
            <div className={styles.chartTableContainer}>
              <ChartTable
                chartData={chartData?.data || []}
                nameTableLabel={t('features.Dashbord.components.ExpensesMonthly.salary_rules')}
                valueTableLabel={t('features.Dashbord.components.ExpensesMonthly.amount')}
                disabled={disabledItems}
                setdisabled={setDisabledItems}
                valueDecimal={2}
                maxHeight={'180px'}
                expensesTable
              />
            </div>
          </>
        )}
      </Section>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  setChart: (key, object) => dispatch(setDashboardData(key, object)),
  fetchExpensesMonthly: target => dispatch(fetchMonthlyExpenses(target))
});

const mapStateToProps = state => ({
  chartData: getChartData(state, 'expensesMonthly')
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpensesMonthly);
